
import { z } from "zod";

// Define fixed plan types
export type SubscriptionPlanType = 'essentials' | 'growth' | 'scale';

// Annual discount percentage
export const ANNUAL_DISCOUNT_PERCENTAGE = 25;

// Get price in cents for payment processing
export const getPriceInCents = (monthlyPrice: number, isAnnual = false): number => {
  if (isAnnual) {
    // For annual plans, return the yearly total in cents with discount
    const discountFactor = (100 - ANNUAL_DISCOUNT_PERCENTAGE) / 100;
    return Math.round(monthlyPrice * 12 * discountFactor * 100);
  }
  
  // For monthly plans, return the monthly price in cents
  return monthlyPrice * 100;
};

// Get monthly price with optional annual discount applied
export const getMonthlyPrice = (plan: string | SubscriptionPlanType, isAnnual = false): number => {
  // Default prices if DB data isn't loaded yet
  const defaultPrices: Record<string, number> = {
    essentials: 499,
    growth: 999,
    scale: 1999,
    trial: 0,
    custom: 1999
  };

  const monthlyPrice = defaultPrices[plan] || 499;
  
  if (isAnnual) {
    const discountFactor = (100 - ANNUAL_DISCOUNT_PERCENTAGE) / 100;
    return monthlyPrice * discountFactor;
  }
  
  return monthlyPrice;
};

// Format price for display with consistent decimal places
export const formatPrice = (price: number): string => {
  // For annual discounted prices, show two decimal places
  // Check if the price has a fractional part
  if (!Number.isInteger(price)) {
    return `$${price.toFixed(2)}`;
  }
  
  // For whole numbers, don't show decimal places
  return `$${price}`;
};

// Map database plan config to UI format
export const mapPlanConfigToUI = (planConfig: any) => {
  console.log(`[${new Date().toISOString()}] [mapPlanConfigToUI] called with:`, JSON.stringify(planConfig, null, 2));

  if (!planConfig) {
    console.error(`[${new Date().toISOString()}] [mapPlanConfigToUI] Invalid plan config provided:`, planConfig);
    return {
      name: "essentials",
      title: "Essentials",
      price: "$4.99",
      description: "Perfect for small businesses and startups",
      features: ["Basic features"],
      plan: "essentials" as SubscriptionPlanType
    };
  }

  try {
    // Create features array using actual values from the plan configuration
    const features: string[] = [
      planConfig.has_unlimited_websites ? 'Unlimited Websites' : `${planConfig.websites_limit || 1} Websites`,
      planConfig.has_unlimited_users ? 'Unlimited Users' : `${planConfig.users_limit || 1} Users`,
      `**${planConfig.company_resolutions_limit}** Company Resolutions`,
      `**${planConfig.person_resolutions_limit}** Person Resolutions`,
    ];

    // Add conditional features
    if (planConfig.has_ai_insights) features.push('AI Insights');
    if (planConfig.has_integrations) features.push('Slack + CRM Integrations');
    if (planConfig.has_email_support) features.push('Email Support');
    
    console.log(`[${new Date().toISOString()}] [mapPlanConfigToUI] Generated features for plan ${planConfig.plan}:`, features);

    const mappedConfig = {
      name: planConfig.plan,
      title: planConfig.plan.charAt(0).toUpperCase() + planConfig.plan.slice(1),
      price: formatPrice(planConfig.monthly_price / 100),
      description: planConfig.plan === 'essentials' 
        ? "Perfect for small businesses and startups"
        : planConfig.plan === 'growth'
        ? "For growing businesses with increasing needs"
        : "For established businesses with high volume needs",
      features,
      plan: planConfig.plan as SubscriptionPlanType
    };

    console.log(`[${new Date().toISOString()}] [mapPlanConfigToUI] Final mapped config for plan ${planConfig.plan}:`, mappedConfig);
    console.log(`[${new Date().toISOString()}] [mapPlanConfigToUI] Features array type check: ${Array.isArray(mappedConfig.features) ? 'Is Array' : 'NOT an Array'}`);
    
    return mappedConfig;
  } catch (error) {
    console.error(`[${new Date().toISOString()}] [mapPlanConfigToUI] Error mapping plan config:`, error);
    // Return fallback config
    return {
      name: planConfig.plan || "essentials",
      title: (planConfig.plan || "Essentials").charAt(0).toUpperCase() + (planConfig.plan || "essentials").slice(1),
      price: formatPrice((planConfig.monthly_price || 599) / 100),
      description: "Basic plan features",
      features: ["Basic features"],
      plan: (planConfig.plan || "essentials") as SubscriptionPlanType
    };
  }
};
